import type { AgenticLogicSurface as AgenticLogicServingSurface } from "@intelligems/ig-types/dtos/agentic-logic/surface";

import { AI_ROUTES, GENERAL_ROUTES } from "@src/routes/routes";

import type { AgenticLogicTab } from "../tabs";
import type { RuleDetailTabKey } from "../workspace/ruleDetailTabs";
import { RULE_DETAIL_TAB_PARAM } from "../workspace/ruleDetailTabs";
import {
  deriveEmbeddedPaths,
  EMBEDDED_RULE_PARAM,
  EMBEDDED_RULE_TAB_PARAM,
  EMBEDDED_SESSION_PARAM,
} from "./deriveEmbeddedPaths";
import {
  NEW_RULE_PARAM_VALUE,
  NEW_RULE_SESSION_PARAM,
  resolveAgenticLogicView,
  RULE_QUERY_PARAM,
} from "./ruleViewParams";

export {
  NEW_RULE_PARAM_VALUE,
  NEW_RULE_SESSION_PARAM,
  RULE_QUERY_PARAM,
  resolveAgenticLogicView,
} from "./ruleViewParams";
export type { AgenticLogicView } from "./ruleViewParams";

/**
 * The same page tree is mounted twice: once for Intelligems staff at
 * `/admin/ai/agentic-logic` and once for merchants at `/rules`. The two mounts
 * differ in their base path, which tabs they expose, and whether the code
 * workspace (Monaco, org secrets, publish gates, version delete) is reachable.
 *
 * Those differences live here as one descriptor rather than as a `basePath`
 * plus two booleans threaded through RulesTab → RulesGrid → RuleActionsMenu →
 * the dialogs: a new audience difference is a new field on this type, not a new
 * prop on a dozen components.
 */
export interface AgenticLogicSurface {
  audience: "admin" | "merchant";
  /**
   * `"standalone"` is one of the two agentic-logic routes, which own their
   * whole URL. `"embedded"` is a rule builder taking over a host's content in
   * place — an upsell settings dialog — whose URL belongs to the host and whose
   * exit is a return to that host, not a navigation to a rules list.
   */
  kind: "standalone" | "embedded";
  /** Page title and the target of every "close"/"back" navigation. */
  title: string;
  /**
   * The surface's ONE route. Which view it shows — list, rule creation, or a
   * rule — is a query param, never a path segment (see {@link RULE_QUERY_PARAM}).
   */
  basePath: string;
  /**
   * Where "back" and "close" land. Standalone surfaces exit to their own list
   * at `basePath`; an embedded surface exits to the host's URL with the rule
   * params stripped, which carries a query string of its own — so this is a
   * separate field from `basePath`, which stays the pathname-only identity key.
   */
  exitPath: string;
  /** Copy for the workspace frame's back button. */
  backLabel: string;
  /** Which query param carries the view; namespaced on embedded surfaces. */
  ruleParam: string;
  /** Which query param carries the rule-detail tab. */
  tabParam: string;
  /**
   * Which query param carries the creation view's Atlas session, so a reload
   * restores the conversation (see `useAtlasChatRail`).
   */
  sessionParam: string;
  rulePath: (ruleId: string, tab?: RuleDetailTabKey) => string;
  /** The rule creation flow, a view of the surface's single route. */
  newRulePath: string;
  /** The rule creation flow, resumed on an in-progress Atlas session. */
  newRuleSessionPath: (sessionId: string) => string;
  /**
   * The tabs this ROUTE exposes to everyone who can open it. The org-data
   * tabs (Datasets, Sources) are never here: they follow the acting user and
   * are added per person by `visibleAgenticLogicTabs`.
   */
  tabs: readonly AgenticLogicTab[];
  /**
   * Embedded surfaces only: the host location this surface was derived from,
   * captured before any rule param was written. Kept so the surface can be
   * reduced back to its serializable descriptor without the host re-supplying
   * what it already handed over.
   */
  host?: { pathname: string; search: string };
  /**
   * Embedded surfaces only: the SERVING surface of the placement the host is
   * editing — checkout for an upsell block, post-purchase for a post-purchase
   * offer, onsite for an experience-builder component.
   *
   * The builder is entered from a placement that already knows where it
   * renders, and everything the author is offered before they type has to
   * agree with it: an "on product pages…" idea offered inside a checkout
   * upsell describes a rule that placement can never run. Standalone surfaces
   * leave it undefined — the rules list is not entered from a placement, and
   * every surface is still open there.
   */
  placementSurface?: AgenticLogicServingSurface;
}

const ruleHref =
  (basePath: string) => (ruleId: string, tab?: RuleDetailTabKey) => {
    const params = new URLSearchParams({ [RULE_QUERY_PARAM]: ruleId });
    if (tab) {
      params.set(RULE_DETAIL_TAB_PARAM, tab);
    }
    return `${basePath}?${params.toString()}`;
  };

const newRuleHref = (basePath: string) =>
  `${basePath}?${RULE_QUERY_PARAM}=${NEW_RULE_PARAM_VALUE}`;

const newRuleSessionHref = (basePath: string) => (sessionId: string) =>
  `${basePath}?${new URLSearchParams({
    [RULE_QUERY_PARAM]: NEW_RULE_PARAM_VALUE,
    [NEW_RULE_SESSION_PARAM]: sessionId,
  }).toString()}`;

export const ADMIN_AGENTIC_LOGIC_SURFACE: AgenticLogicSurface = {
  audience: "admin",
  kind: "standalone",
  title: "Agentic Logic",
  basePath: AI_ROUTES.AiAgenticLogic,
  exitPath: AI_ROUTES.AiAgenticLogic,
  backLabel: "All rules",
  ruleParam: RULE_QUERY_PARAM,
  tabParam: RULE_DETAIL_TAB_PARAM,
  sessionParam: NEW_RULE_SESSION_PARAM,
  rulePath: ruleHref(AI_ROUTES.AiAgenticLogic),
  newRulePath: newRuleHref(AI_ROUTES.AiAgenticLogic),
  newRuleSessionPath: newRuleSessionHref(AI_ROUTES.AiAgenticLogic),
  // Assignments is staff-only until placement management is merchant-ready.
  // The org-data tabs (Datasets, Sources) are on NO surface's list: they
  // follow the acting user, not the route — see `visibleAgenticLogicTabs`.
  tabs: ["rules", "assignments", "exclusions"],
};

export const MERCHANT_AGENTIC_LOGIC_SURFACE: AgenticLogicSurface = {
  audience: "merchant",
  kind: "standalone",
  title: "Rules",
  basePath: GENERAL_ROUTES.Rules,
  exitPath: GENERAL_ROUTES.Rules,
  backLabel: "All rules",
  ruleParam: RULE_QUERY_PARAM,
  tabParam: RULE_DETAIL_TAB_PARAM,
  sessionParam: NEW_RULE_SESSION_PARAM,
  rulePath: ruleHref(GENERAL_ROUTES.Rules),
  newRulePath: newRuleHref(GENERAL_ROUTES.Rules),
  newRuleSessionPath: newRuleSessionHref(GENERAL_ROUTES.Rules),
  // Merchants manage their reusable rules and the org-wide blocklist that
  // constrains every rule. Assignments is hidden until placement management
  // is merchant-ready. An Intelligems admin opening this route still gets the
  // org-data tabs, added per acting user by `visibleAgenticLogicTabs`.
  tabs: ["rules", "exclusions"],
};

/**
 * The descriptor for a rule builder that has taken over a host's content in
 * place — an upsell settings dialog, the onsite experience builder. Derived
 * from the HOST's own location, captured before any rule param was written, so
 * the host's query string rides along on every view and is restored exactly on
 * exit (see `deriveEmbeddedPaths`).
 *
 * Merchant-shaped by construction: every host is a merchant surface, and an
 * embedded builder never shows list tabs, since it has no list.
 */
export const embeddedAgenticLogicSurface = ({
  hostPathname,
  hostSearch,
  title,
  backLabel,
  placementSurface,
}: {
  hostPathname: string;
  hostSearch: string;
  title: string;
  backLabel: string;
  placementSurface: AgenticLogicServingSurface;
}): AgenticLogicSurface => {
  const paths = deriveEmbeddedPaths(hostPathname, hostSearch, {
    ruleParam: EMBEDDED_RULE_PARAM,
    tabParam: EMBEDDED_RULE_TAB_PARAM,
    sessionParam: EMBEDDED_SESSION_PARAM,
  });
  return {
    audience: "merchant",
    kind: "embedded",
    title,
    basePath: hostPathname,
    exitPath: paths.exitPath,
    backLabel,
    ruleParam: EMBEDDED_RULE_PARAM,
    tabParam: EMBEDDED_RULE_TAB_PARAM,
    sessionParam: EMBEDDED_SESSION_PARAM,
    rulePath: paths.rulePath,
    newRulePath: paths.newRulePath,
    newRuleSessionPath: paths.newRuleSessionPath,
    tabs: [],
    host: { pathname: hostPathname, search: hostSearch },
    placementSurface,
  };
};

/**
 * Which editor a rule opens into: template-managed rules get the param editor,
 * code-managed rules get the rule workspace (Selection logic graph + Advanced
 * code tab) on every surface. Merchants and admins see the same workspace; the
 * surfaces differ only in base path and which list tabs they expose.
 */
export const ruleWorkspaceKind = (
  managedBy: "builder" | "code"
): "template" | "code" => (managedBy === "builder" ? "template" : "code");

const normalizePathname = (pathname: string) =>
  pathname.replace(/\/+$/, "") || "/";

/**
 * For surfaces rendered outside the page tree — the global AI chat panel, which
 * navigates to a rule it just created but sits in the layout shell rather than
 * under the provider. Resolved from the pathname so the chat lands the merchant
 * back on `/rules?rule=…`, not on an admin route they cannot open.
 */
export const surfaceForPathname = (pathname: string): AgenticLogicSurface =>
  normalizePathname(pathname) === MERCHANT_AGENTIC_LOGIC_SURFACE.basePath
    ? MERCHANT_AGENTIC_LOGIC_SURFACE
    : ADMIN_AGENTIC_LOGIC_SURFACE;

/**
 * Whether a pathname is one of the two agentic-logic surfaces. Exact, not a
 * prefix: each surface is a single route now, so anything deeper is a different
 * page (or a legacy path on its way to a redirect).
 */
export const isAgenticLogicPathname = (pathname: string): boolean => {
  const normalized = normalizePathname(pathname);
  return (
    normalized === MERCHANT_AGENTIC_LOGIC_SURFACE.basePath ||
    normalized === ADMIN_AGENTIC_LOGIC_SURFACE.basePath
  );
};

/**
 * Whether the Atlas chat rail is pinned open at a location. The rail belongs
 * to the views where a rule is being written — the creation flow and the rule
 * workspace — not to the rules list, which is a browse page whose own layout
 * runs edge to edge. So the list sits on the surface but outside the pinned
 * region: arriving on it ends the chat session and closes the panel (see
 * `LoggedInProvider`), and leaving the surfaces entirely still does.
 *
 * An embedded surface has no pathname of its own — its host is a dialog, which
 * the URL does not describe — so it is passed in explicitly by whichever
 * provider has registered it, and its own namespaced param decides the view.
 */
export const isAtlasChatLocation = (
  pathname: string,
  search: string,
  activeSurface?: AgenticLogicSurface | null
): boolean => {
  if (activeSurface?.kind === "embedded") {
    return (
      resolveAgenticLogicView(
        search,
        activeSurface.ruleParam,
        activeSurface.sessionParam
      ).kind !== "list"
    );
  }
  return (
    isAgenticLogicPathname(pathname) &&
    resolveAgenticLogicView(search).kind !== "list"
  );
};

/**
 * The serializable form of a surface, as it is registered in redux while a
 * surface is on screen. The path builders are functions, so what is stored is
 * the small set of facts they are built from and
 * {@link agenticLogicSurfaceFromDescriptor} rebuilds the descriptor.
 */
export type AgenticLogicSurfaceDescriptor =
  | { kind: "standalone"; audience: "admin" | "merchant" }
  | {
      kind: "embedded";
      title: string;
      backLabel: string;
      hostPathname: string;
      hostSearch: string;
      placementSurface: AgenticLogicServingSurface;
    };

export const agenticLogicSurfaceDescriptor = (
  surface: AgenticLogicSurface
): AgenticLogicSurfaceDescriptor =>
  surface.kind === "embedded"
    ? {
        kind: "embedded",
        title: surface.title,
        backLabel: surface.backLabel,
        hostPathname: surface.host?.pathname ?? surface.basePath,
        hostSearch: surface.host?.search ?? "",
        // Non-null by construction: `embeddedAgenticLogicSurface` requires it,
        // and it is the only builder of an embedded surface.
        placementSurface:
          surface.placementSurface as AgenticLogicServingSurface,
      }
    : { kind: "standalone", audience: surface.audience };

export const agenticLogicSurfaceFromDescriptor = (
  descriptor: AgenticLogicSurfaceDescriptor
): AgenticLogicSurface => {
  if (descriptor.kind === "embedded") {
    return embeddedAgenticLogicSurface({
      hostPathname: descriptor.hostPathname,
      hostSearch: descriptor.hostSearch,
      title: descriptor.title,
      backLabel: descriptor.backLabel,
      placementSurface: descriptor.placementSurface,
    });
  }
  return descriptor.audience === "merchant"
    ? MERCHANT_AGENTIC_LOGIC_SURFACE
    : ADMIN_AGENTIC_LOGIC_SURFACE;
};
